import { useEffect, useState, useMemo } from "react";
import { Plus, Search, Filter, CalendarX } from "lucide-react";
import moment from "moment";

import EventTable from "./components/EventTable";
import CreateEventModal from "./components/CreateEventModal";
import { AdminEventAPI } from "../../services/adminEvent.api";
import { TableSkeleton } from "../../components/Skeleton";
import "./Events.scss";

const STATUS_OPTIONS = ["All", "draft", "published", "closed"];

export default function AdminEvents() {
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  /* ================= UPCOMING TABLE STATE ================= */
  const [upcomingSearch, setUpcomingSearch] = useState("");
  const [upcomingFilter, setUpcomingFilter] = useState("All");

  /* ================= PAST TABLE STATE ================= */
  const [pastSearch, setPastSearch] = useState("");
  const [pastFilter, setPastFilter] = useState("All");

  /* ================= FETCH ================= */
  async function fetchEvents() {
    try {
      setLoading(true);
      const res = await AdminEventAPI.list();
      setAllEvents(res.data?.items || []);
    } catch (err) {
      console.error("Failed load events", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchEvents();
  }, []);

  /* ================= SPLIT EVENTS ================= */
  const { upcoming, past } = useMemo(() => {
    return {
      upcoming: allEvents.filter((e) => !e.isPast),
      past: allEvents.filter((e) => e.isPast),
    };
  }, [allEvents]);

  /* ================= FILTERED HELPERS ================= */
  function filterEvents(events, search, filterStatus) {
    return events.filter((e) => {
      const matchesSearch =
        !search ||
        e.title?.toLowerCase().includes(search.toLowerCase()) ||
        e.location?.name?.toLowerCase().includes(search.toLowerCase());

      const matchesFilter =
        filterStatus === "All" || e.status === filterStatus;

      return matchesSearch && matchesFilter;
    });
  }

  const upcomingFiltered = filterEvents(upcoming, upcomingSearch, upcomingFilter);
  const pastFiltered = filterEvents(past, pastSearch, pastFilter);

  /* ================= EDIT ================= */
  function handleEdit(event) {
    setEditingEvent(event);
  }

  /* ================= DELETE ================= */
  async function handleDelete(event) {
    const ok = window.confirm(
      `Archive event "${event.title}"?\nEvent will no longer be visible.`
    );
    if (!ok) return;

    try {
      await AdminEventAPI.remove(event._id);
      setAllEvents((prev) => prev.filter((e) => e._id !== event._id));
    } catch (err) {
      alert("Failed archive event");
      console.error(err);
    }
  }

  return (
    <div className="admin-events">
      {/* ================= HEADER ================= */}
      <div className="dashboard-header">
        <div>
          <h1>Events</h1>
          <p>Manage all events</p>
        </div>

        <button
          className="btn btn-primary"
          onClick={() => setShowCreate(true)}
        >
          <Plus size={16} />
          Create Event
        </button>
      </div>

      {/* ================= UP COMING EVENTS ================= */}
      <div className="events-section">
        <div className="section-header">
          <div className="section-title-row">
            <h2 className="section-title">Upcoming Events</h2>
            <span className="section-count">{upcomingFiltered.length}</span>
          </div>

          <div className="section-controls">
            <div className="search-wrap">
              <Search size={15} className="search-icon" />
              <input
                className="search-input"
                placeholder="Cari event..."
                value={upcomingSearch}
                onChange={(e) => setUpcomingSearch(e.target.value)}
              />
            </div>

            <div className="filter-wrap">
              <Filter size={14} className="filter-icon" />
              <select
                className="filter-select"
                value={upcomingFilter}
                onChange={(e) => setUpcomingFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s === "All" ? "Semua Status" : s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-body">
            {loading ? (
              <TableSkeleton rows={3} />
            ) : upcomingFiltered.length > 0 ? (
              <EventTable
                events={upcomingFiltered}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onCreate={() => setShowCreate(true)}
              />
            ) : (
              <EmptyState
                message={
                  upcomingSearch || upcomingFilter !== "All"
                    ? `Tidak ada eventUpcoming yang cocok`
                    : "Belum ada upcoming event"
                }
              />
            )}
          </div>
        </div>
      </div>

      {/* ================= PAST EVENTS ================= */}
      <div className="events-section">
        <div className="section-header">
          <div className="section-title-row">
            <h2 className="section-title">Past Events</h2>
            <span className="section-count">{pastFiltered.length}</span>
          </div>

          <div className="section-controls">
            <div className="search-wrap">
              <Search size={15} className="search-icon" />
              <input
                className="search-input"
                placeholder="Cari event..."
                value={pastSearch}
                onChange={(e) => setPastSearch(e.target.value)}
              />
            </div>

            <div className="filter-wrap">
              <Filter size={14} className="filter-icon" />
              <select
                className="filter-select"
                value={pastFilter}
                onChange={(e) => setPastFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s === "All" ? "Semua Status" : s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-body">
            {loading ? (
              <TableSkeleton rows={3} />
            ) : pastFiltered.length > 0 ? (
              <EventTable
                events={pastFiltered}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onCreate={() => setShowCreate(true)}
              />
            ) : (
              <EmptyState
                message={
                  pastSearch || pastFilter !== "All"
                    ? `Tidak ada eventPast yang cocok`
                    : "Tidak ada event yang lalu"
                }
              />
            )}
          </div>
        </div>
      </div>

      {showCreate && (
        <CreateEventModal
          onClose={() => setShowCreate(false)}
          onCreated={(e) => {
            setAllEvents((prev) => [e, ...prev]);
            setShowCreate(false);
          }}
        />
      )}

      {editingEvent && (
        <CreateEventModal
          mode="edit"
          initialData={editingEvent}
          onClose={() => setEditingEvent(null)}
          onCreated={(updated) => {
            setAllEvents((prev) =>
              prev.map((e) => (e._id === updated._id ? updated : e))
            );
            setEditingEvent(null);
          }}
        />
      )}
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="events-empty">
      <div className="events-empty__icon">
        <CalendarX size={36} />
      </div>
      <p className="events-empty__message">{message}</p>
    </div>
  );
}